/**
 * @preserve [gmap] this is a async loader and wrapper over various google.map.* APIs | Copyright 2025 digiconcept
 */

import routing from 'modules/route.js'
import debounce from 'debounce'

const l = 'dc_gmap_setup'
const w = window
const d = w.document

const GOOGLE_API_KEY = (function () {
  const script = d.currentScript || d.querySelector('script[data-google-api-key]')
  return (script && script.hasAttribute('data-google-api-key')) ? script.getAttribute('data-google-api-key') : ''
})()

const invokeAll = function (arr) {
  arr.forEach(Function.prototype.call, Function.prototype.call)
}

// GMAP_ENABLE_DEBUG and log will be auto-elided in -min.js
export const log = function () {
  if (GMAP_ENABLE_DEBUG) {
    console.log.apply(console, arguments) // eslint-disable-line no-console
  }
}
export const info = function () {
  if (GMAP_ENABLE_DEBUG) {
    console.info.apply(console, arguments) // eslint-disable-line no-console
  }
}
export const warn = function () {
  console.warn.apply(console, arguments) // eslint-disable-line no-console
}
export const error = function () {
  console.error.apply(console, arguments) // eslint-disable-line no-console
}

const loadDep = function (src, async, f) {
  const s = d.createElement('script')
  log('loading', src)
  f && (function () {
    s.onreadystatechange = function () {
      if (this.readyState === 'complete') {
        f()
        log('loaded', src)
      }
    }
    s.onload = function () {
      f()
      log('loaded', src)
    }
  })()

  s.type = 'text/javascript'
  s.setAttribute('src', src)
  s.async = async

  d.head.appendChild(s)
}

let geo2LatLng_ = function () {
  error('google.* not yet loaded')
}

let loadLocation = geo2LatLng_

/*
 * loader and fuction provider
 */
const DC_GMAP_API = function () {
  const initN = 'initialize_dc_gmap'

  /** @type {boolean} */
  let depsLoaded = false

  /** @type {boolean} */
  let depsLoading = false

  /** @type {Array} */
  const todos = []

  const initialize = function () {
    info('gmap.deps.loaded')
    w[initN] = null
    delete w[initN]
    depsLoaded = true

    initFns()

    log('processing todos')
    todos.forEach(processFD)
  }

  /**
   * @param {!string|!array} ll
   * @param {!function} success
   * @param {!function} error
   */
  let getAddressFromCoords = function (ll, s, e) {
    error('google.* not yet loaded')
  }

  const processFD = function (fd) {
    if (typeof fd[1] === 'function') { fd[1].call() } else { fd[0].apply(undefined, fd[1]) }
  }

  const loadDeps = function () {
    const apiEndpoint = new URL('https://maps.googleapis.com/maps/api/js')
    apiEndpoint.search = new URLSearchParams({
      v: GOOGLE_MAPS_API_VERSION,
      key: GOOGLE_API_KEY,
      libraries: 'geometry,marker',
      callback: initN,
      loading: 'async'
    })

    depsLoading = true
    w[initN] = initialize
    loadDep(apiEndpoint.toString(), true)
  }

  const initFns = function () {
    log('initFns')

    const geocoder = new google.maps.Geocoder()

    geo2LatLng_ = function (addr, callback) {
      geocoder.geocode({
        address: addr
      }, callback)
    }

    loadLocation = function (addr, m, cb) {
      geo2LatLng_(addr, function (results, status) {
        log('geocoder request', status)
        if (status === google.maps.GeocoderStatus.OK) {
          m.setCenter(results[0].geometry.location)
          cb && cb(results[0])
        } else {
          warn('Unable to load location ' + addr + ' due to ' + status)
          cb && cb(false)
        }
      })
    }

    /**
     * @param {!string|!array} ll
     * @param {!function} success
     * @param {!function} error
     */
    getAddressFromCoords = function (ll, success, error) {
      const latlngarray = typeof ll === 'string' ? ll.split(',', 2) : ll
      const location = {
        lat: parseFloat(latlngarray[0]),
        lng: parseFloat(latlngarray[1])
      }
      geocoder.geocode({ location }, function (results, status) {
        status === 'OK' ? success(results[0], status) : error(results, status)
      })
    }
  }

  /**
   * @param {string} id
   * @param {(google.maps.LatLng|google.maps.LatLngLiteral)} latlng
   * @param {number} zoom
   */
  const centerOn = function (id, latlng, zoom) {
    if (!(id in maps)) {
      return false
    }
    const m = maps[id]
    m.setCenter(latlng)
    if (zoom !== m.getZoom()) {
      m.setZoom(zoom)
    }
  }

  /**
   * @param {string} id
   * @param {function()} fn
   * @return {Array|false}
   */
  const mapOverMarkers = function (id, fn) {
    return (maps[id].cleanables) ? maps[id].cleanables.map(fn) : false
  }

  return {
    onLoad: function (fd) {
      if (!depsLoaded) {
        if (!depsLoading) {
          loadDeps()
        }
        todos.push(fd)
      } else {
        processFD(fd)
      }
    },
    geo2LatLng: function (s, cb, country) {
      geo2LatLng_(s, function (results, status) {
        if (status === google.maps.GeocoderStatus.OK && results[0] &&
          (!country || results[0].address_components.find((ac) => ac.short_name === country))) {
          const loc = results[0].geometry.location
          cb({
            lat: loc.lat(),
            lng: loc.lng()
          }, results[0].address_components || {})
        } else {
          cb(false, {})
        }
      })
    },
    latLng2Geo: function (ll, s, e) {
      return getAddressFromCoords(ll, s, e)
    },
    centerOn,
    mapOverMarkers,
    repaint: afterWindowResize
  }
}

const classNames = {
  container: 'dc-gmap-container',
  map: 'dc-gmap'
}

const DCMAP_DEFAULTS = {
  lib: '/src_core/javascript/gmap/lib/',
  'no-resize-handling': false,
  'no-map-reuse': false,
  markers: [],
  icons: {},
  perimeter: {
    show: false,
    radius: 15,
    fillColor: '#007B29',
    strokeWeight: 1,
    strokeColor: 'black',
    strokeOpacity: 1,
    fillOpacity: 0.5
  }
}

/** @see: https://developers.google.com/maps/documentation/javascript/3.24/reference#MapOptions */
const GMAP_DEFAULTS = {
  scrollwheel: false,
  mapId: '2592b541e11ae5a0'
}

/** @const */
const FALLBACK_ZOOM = 8

const setup = function (f) {
  const gapi = new DC_GMAP_API()

  const pushF = function (data) {
    log('push', data)
    gapi.onLoad([f, data])
  }

  if (w[l] && Array.isArray(w[l])) {
    w[l].forEach(pushF)
  }

  log('injecting push handler into [' + l + ']')
  w[l] = {
    push: pushF
  }

  return gapi
}

const afterWindowResize = function () {
  log('Event:afterWindowResize - handling resize on all maps')
  for (const id in maps) {
    maps[id]['handle-resize'] && maps[id]['handle-resize']()
  }
}

const simpleMerge = function (o1, o2) {
  for (const attrname in o2) {
    o1[attrname] = o2[attrname]
  }
  return o1
}

const mkMarker = function (map, mSpec, dcopts, iw) {
  const icons = dcopts.icons
  const eventCallbacks = dcopts.events

  if (!mSpec.icon && icons.markers) { // custom icon for specific
    // marker
    const pinImg = document.createElement('img')
    pinImg.src = Array.isArray(icons.markers)
      ? icons.markers[0]
      : icons.markers

    mSpec.content = pinImg
  }

  /** @type {google.maps.marker.AdvancedMarkerElementOptions} */
  const { position, title, zIndex, content } = mSpec
  const m = new google.maps.marker.AdvancedMarkerElement({ map, position, title, zIndex, content })
  m.data = mSpec.data
  m.zoomSens = true

  if (iw) {
    google.maps.event.addListener(m, 'click', function () {
      log('Event:click')
      iw.refresh(m.data || {})
      iw.open(map, m)
    })
  } else {
    for (const eventname in eventCallbacks) {
      if (eventCallbacks[eventname].marker) {
        google.maps.event.addListener(m, eventname, eventCallbacks[eventname].marker.bind(m))
      }
    }
  }
  return m
}

const createLogo = function (e, logo, scale) {
  const img = d.createElement('img')
  img.src = logo
  img.style.zIndex = 1
  img.style.position = 'absolute'
  img.style.pointerEvents = 'none'
  img.onload = function () {
    const w = img.clientWidth * scale
    const h = img.clientHeight * scale
    img.style.width = w + 'px'
    img.style.left = Math.floor((e.clientWidth - w) / 2) + 'px'
    img.style.top = Math.floor((e.clientHeight - h) / 2) + 'px'
  }
  e.appendChild(img)
}

const isNode = function (o) {
  return o.nodeName || false
}

const uniq = function (a) {
  return a.sort().filter(function (el, i, a) {
    return i === a.indexOf(el)
  })
}

/**
 * @param {HTMLElement|string}
 *            selectorOrNode
 */
const handleDom = function (selectorOrNode) {
  /* handle the DOM */
  const e = isNode(selectorOrNode)
    ? selectorOrNode
    : (d.getElementById(selectorOrNode) || d.getElementsByClassName(selectorOrNode)[0])

  if (!e) {
    throw new Error('selectorOrNode [' + selectorOrNode + '] not valid')
  }

  e.parentNode.className = uniq(
    e.parentNode.className.split(' ').concat(classNames.container)
      .filter(function (v) {
        return !!v
      })).join(' ')
  e.className = uniq(
    e.className.split(' ').concat(classNames.map).filter(
      function (v) {
        return !!v
      })).join(' ')

  return e
}

const createInfoWindow = function (iwOpts) {
  const iw = new google.maps.InfoWindow(iwOpts)
  const iwF = iwOpts.refresh
  iw.refresh = function (data) {
    this.setContent(iwF(data))
  }.bind(iw)
  return iw
}

/**
 * Gets a google.maps.LatLngBounds for provider marker array
 *
 * @param {Array
 *            <google.maps.marker.AdvancedMarkerElement>} mx the marker array
 */
const getBB = function (mx) {
  const bb = new google.maps.LatLngBounds()
  mx.forEach(function (marker) {
    bb.extend(new google.maps.LatLng(marker.position.lat, marker.position.lng))
  })
  return bb
}

/**
 * @param {google.maps.Map}
 *            map
 * @param {Object}
 *            dcopts
 * @param {Array
 *            <google.maps.InfoWindow>} iws
 */
const processMarkers = function (map, dcopts, iws) {
  return dcopts.markers.map(function (marker, i) {
    return mkMarker(map, marker, dcopts, iws[i] || iws[0] || false)
  })
}

/**
 * @param {google.maps.Map} map
 *
 */
const drawPerimeter = function (map, opts, center) {
  const marker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position: opts.position || center || map.getCenter()
  })

  if (opts.icon) {
    const pinImg = document.createElement('img')
    pinImg.src = opts.icon

    marker.content = pinImg
  }

  marker.visible = !opts.noMarker

  const circle = new google.maps.Circle({
    ...opts,
    map,
    radius: opts.radius * 1000,
    center: marker.position
  })

  return {
    circle,
    marker
  }
}

/** @type {Object<string,google.maps.Map>} */
const maps = {}

/**
 * @param {number} mapDomE
 * @param {Object<string, *>} gopts
 * @param {boolean} reuse
 */
const getOrCreateNewMap = function (mapDomE, gopts, reuse, onZoomChange) {
  const hash = mapDomE.id
  if (maps[hash]) {
    maps[hash].cleanables.forEach(function (c) {
      if (!c) { return }

      if (c.clearMarkers) {
        // cleanup MarkerClusterer
        c.clearMarkers()
      } else if (c.remove) {
        // cleanup MarkerClusterer
        c.remove()
      } else {
        // cleanup Markers
        c.setMap && c.setMap(null)
      }
      c = null
    })
    maps[hash].cleanables = []
  }
  if (!reuse || !maps[hash]) {
    log('creating NEW map in', hash)
    maps[hash] = new google.maps.Map(mapDomE, gopts)
    maps[hash].setGeoLocation = function (l, cb) {
      loadLocation(l, this, cb)
    }
    maps[hash].cleanables = []
    if (onZoomChange) {
      google.maps.event.addListener(maps[hash], 'zoom_changed', onZoomChange.bind(maps[hash]))
    }
  }
  return maps[hash]
}

/**

 * @param {!google.maps.Map}
 *            map
 * @param {Array<number>}
 *            sps
 * @param {number}
 *            minMarker
 */
const getMarkerImgIndex4ZoomLevel = function (map, sps, minMarker) {
  if (minMarker && (map.markerCount < minMarker)) {
    return sps.length
  }

  const z = map.getZoom() || 16
  const spsl = sps.length

  let use = 0

  for (let i = (spsl - 1); i > -1; i -= 1) {
    if (z > sps[i]) {
      use = (i + 1)
      break
    }
  }
  return use
}

/**
 * @param {!Object} dcopts
 * @param {!google.maps.Map}  map
 */
const markersAsOverlay = function (dcopts, map) {
  import(/* webpackChunkName: "overlaymarker" */ 'overlaymarker')
    .then(({ HTMLContentOverlayMarker }) => {
      dcopts.markers.forEach(function (m) {
        new HTMLContentOverlayMarker(map, m, dcopts.markersAsOverlay.getOverlayContent, dcopts) // eslint-disable-line no-new
      })
    })
}

/**
 * @param {!Object}  dcopts
 * @param {Array.<google.maps.marker.AdvancedMarkerElement>} markers
 * @param {!google.maps.Map} map
 */
const markersAsCluster = function (dcopts, markers, map) {
  import(/* webpackChunkName: "markerclusterer" */ 'modules/markerclusterer')
    .then(({ MarkerClusterer, DefaultRenderer, SVGRenderer, ImgRenderer }) => {
      info('>> markersAsCluster [', dcopts.markers.length, ']')

      // disable default click
      dcopts.markersAsCluster.zoomOnClick = !dcopts.markersAsCluster.onClusterClick

      const clusterConfig = dcopts.markersAsCluster?.styles[0] || dcopts.markersAsCluster?.styles
      const firstURL = String(clusterConfig?.url)

      const R = firstURL.startsWith('<svg ') ? SVGRenderer : ImgRenderer

      const renderer = firstURL
        ? new R(dcopts.markersAsCluster.styles)
        : (new DefaultRenderer())

      const clusterer = new MarkerClusterer({ map, markers, renderer })

      if (dcopts.markersAsCluster.onClusterClick) {
        google.maps.event.addListener(clusterer, 'clusterclick', dcopts.markersAsCluster.onClusterClick)
      }

      map.cleanables.push(clusterer)
    })
}

function migrateOptions (orig) {
  const gmapopts = { ...orig }
  for (const [o, n] of [['draggable', 'gestureHandling']]) {
    if (Object.hasOwn(gmapopts, o)) {
      gmapopts[n] = gmapopts[o]
      delete gmapopts[o]
    }
  }
  return gmapopts
}
const createMap = function (selectorOrNode, dcgmapopts, gmapopts, postFn) {
  gmapopts = migrateOptions(gmapopts)

  const mapDomE = handleDom(selectorOrNode)
  const mapCalls = []
  const gopts = [{}, GMAP_DEFAULTS, gmapopts || {}].reduce(simpleMerge)
  const dcopts = [{}, DCMAP_DEFAULTS, dcgmapopts || {}].reduce(simpleMerge)
  const zoom = gmapopts.zoom || gopts.zoom || false

  delete gopts.zoom

  if (dcopts.logo) {
    createLogo(mapDomE, dcopts.logo, dcopts.logoScale || 1)
    gopts.noClear = true
    gopts.gestureHandling = false
  }

  const hasMarkers = dcopts.markers && Array.isArray(dcopts.markers) && dcopts.markers.length > 0
  const viaOverlay = hasMarkers && dcopts.markersAsOverlay && dcopts.markersAsOverlay.getOverlayContent
  const viaCluster = hasMarkers && !viaOverlay && dcopts.markersAsCluster

  let bb = new google.maps.LatLngBounds()

  if (hasMarkers) {
    dcopts.markers = dcopts.markers.map(function (mSpec) {
      return Array.isArray(mSpec)
        ? { position: new google.maps.LatLng(mSpec[0], mSpec[1]) }
        : mSpec
    })
    bb = getBB(dcopts.markers)
  }

  gopts.center = gopts.center || (bb.isEmpty() ? null : bb?.getCenter()) || new google.maps.LatLng(47.679, 13.147)

  let onZoom = null

  if (dcopts.icons.switchPoints) {
    const sps = dcopts.icons.switchPoints
    const icons = dcopts.icons.markers || []
    const minMarkers = dcopts.icons.minMarkers
    const specs = dcopts.icons.specs || {}
    if (sps.length && (icons.length - 1 !== sps.length)) {
      warn('MarkerIcons and SwitchPoints are not configured correctly')
    }
    onZoom = function () {
      const use = getMarkerImgIndex4ZoomLevel(this, sps, minMarkers)
      const spec = specs[use]
      const src = icons[use]

      log('zoomed to', this.getZoom(), 'using', use)

      if (src) {
        log('Swapping Icons due to zoom, using', use, src, spec)

        this.cleanables && this.cleanables.forEach(function (c) {
          if (c.zoomSens && c.content) {
            c.content.src = src
          }
        })
      }
    }
  }

  const m = getOrCreateNewMap(mapDomE, gopts, !dcopts['no-map-reuse'], onZoom)
  m.markerCount = hasMarkers ? dcopts.markers.length : 0

  if (dcopts.mapTypes && dcopts.mapTypes.length) {
    dcopts.mapTypes.forEach(function (mt) {
      m.mapTypes.set(mt.id, new google.maps.StyledMapType(
        mt.config, {
          map: m,
          name: mt.name
        }))
      if (mt.active) {
        info('activating ', mt.name, mt.id)
        m.setMapTypeId(mt.id)
      }
    })
  }

  let route
  if (routing.isRequested(dcopts)) {
    route = routing.create(m, dcopts)
    gopts.center = null
    mapCalls.push(function () {
      const bb = route.getBounds()
      log('panToBounds+setCenter', bb)
      bb && m.setCenter(bb.getCenter())
      bb && m.panToBounds(bb)
    })
  } else if (viaOverlay) {
    markersAsOverlay(dcopts, m)
  }

  if (!hasMarkers && dcopts.location) { // prefer markers, but if non center on location
    m.setGeoLocation(
      dcopts.location
      , dcopts.locationMarker
        ? function (r) { // if locationMarker put marker on location
          log('created location centerMarker')

          if (r?.geometry?.location) {
            // eslint-disable-next-line no-new
            new google.maps.marker.AdvancedMarkerElement({
              position: r.geometry.location,
              map: m
            })
            mapCalls.push(function () {
              log('setCenter', l)
              m.setCenter(l)
            })
          }
        }
        : function () { })
  }

  const processMarkersAndInfoWindows = function (dcopts) {
    let iws = []
    if (dcopts.infoWindow && dcopts.infoWindow.refresh) {
      iws = dcopts.infoWindow.multiple
        ? (dcopts.markers || []).map(() => createInfoWindow(dcopts.infoWindow))
        : [createInfoWindow(dcopts.infoWindow)]
    }
    return processMarkers(m, dcopts, iws)
  }

  const processedMarkers = (hasMarkers && !viaOverlay)
    ? processMarkersAndInfoWindows(dcopts)
    : []

  const perimeter = (dcopts.perimeter && dcopts.perimeter.show)
    ? drawPerimeter(m, dcopts.perimeter, gopts.center)
    : false

  if (viaCluster) {
    gopts.maxZoom && (dcopts.markersAsCluster.maxZoom = gopts.maxZoom - 1)
    markersAsCluster(dcopts, processedMarkers, m)
  }

  if (perimeter && m.getRenderingType() !== google.maps.RenderingType.UNINITIALIZED) {
    bb = perimeter.circle.getBounds()
  }

  if (perimeter || processedMarkers.length > 1 || (viaOverlay && !zoom)) {
    log('google.maps.Map.prototype.fitBounds.bind', bb.toJSON(), { padding: 15 })
    mapCalls.push(google.maps.Map.prototype.fitBounds.bind(m, bb, 15))
  } else if (!routing.isRequested(dcopts)) {
    log('google.maps.Map.prototype.setZoom.bind', zoom || FALLBACK_ZOOM)
    mapCalls.push(google.maps.Map.prototype.setZoom.bind(m, zoom || FALLBACK_ZOOM))
  }

  if (gopts.center) {
    log('google.maps.Map.prototype.setCenter.bind', gopts.center)
    mapCalls.push(google.maps.Map.prototype.setCenter.bind(m, gopts.center))
  }

  if (perimeter && perimeter.circle) {
    log('setup perimeter handler', perimeter)

    const perimeterDisplayHandler = debounce(function () {
      info('changed*:perimeter handler triggered')

      /** @type google.maps.Circle */
      const circle = perimeter.circle

      if (!circle) { return }

      /** @type google.maps.Map */
      const map = circle.getMap()

      if (!map) { return }

      /** @type google.maps.LatLngBounds */
      const mapbounds = map.getBounds()

      /** @type google.maps.LatLngBounds */
      const circlebounds = circle.getBounds()

      if (!mapbounds || !circlebounds) { return }

      /** @type google.maps.LatLngBounds */
      const union = mapbounds.union(circlebounds)

      /** @type boolean, map is fully within cirle bounds */
      const within = circlebounds.toString() === union.toString()

      log('map bounds', mapbounds.toString())
      log('circle bounds', circlebounds.toString())
      log('union', union.toString())
      log('union === map ?', within)

      circle.setVisible(!within)
    }, 100)

    m.cleanables.push(google.maps.event.addListener(m, 'zoom_changed', perimeterDisplayHandler))
    m.cleanables.push(google.maps.event.addListener(m, 'bounds_changed', perimeterDisplayHandler))
  }

  google.maps.event.addListenerOnce(m, 'idle', function () {
    log('Event:idle')
    invokeAll(mapCalls)
    postFn && postFn.call(m)
  })

  invokeAll(mapCalls)

  if (!dcopts['no-resize-handling']) {
    m['handle-resize'] = function () {
      invokeAll(mapCalls)
      // by the books:
      // Developers should trigger this event on the map when the div
      // changes size
      google.maps.event.trigger(m, 'resize')
    }
  }

  m.cleanables = m.cleanables.concat(
    processedMarkers,
    perimeter
      ? [perimeter.circle, perimeter.marker]
      : [])

  info('Cleanables after load', m.cleanables.length)

  google.maps.event.trigger(m, 'zoom_changed')
}

// attach resize handlers
w.addEventListener('resize', debounce(afterWindowResize, 250), false)
w.addEventListener('orientationchange', debounce(afterWindowResize, 231), false)

const gapi = setup(createMap)
info('gmap.loaded')
window.dc_gmap = gapi
